import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Eye, EyeSlash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForceChangePassword({ onSuccess }) {
  const { t } = useTranslation();
  const { direction } = useLanguage();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error(t('password_min_length'));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t('passwords_dont_match'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, { new_password: newPassword }, { withCredentials: true });
      toast.success(t('password_changed_success'));
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('password_change_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F2F0FA] flex items-center justify-center p-4" dir={direction}>
      <Card className="w-full max-w-md border-[#C4BFF0] shadow-lg" data-testid="force-change-password">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-[#655dc6]/10 flex items-center justify-center">
            <Lock size={32} weight="duotone" className="text-[#655dc6]" />
          </div>
          <CardTitle className="text-2xl text-[#655dc6]">{t('change_password')}</CardTitle>
          <p className="text-sm text-[#655dc6]/60 mt-2">{t('first_login_change_password')}</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#655dc6]/80">{t('new_password')}</Label>
              <div className="relative">
                <Input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-[#E8E6F5] border-[#C4BFF0] pe-10"
                  placeholder="******"
                  data-testid="new-password-input"
                />
                <button type="button" onClick={() => setShowNew(!showNew)} className="absolute end-3 top-1/2 -translate-y-1/2 text-[#655dc6]/50">
                  {showNew ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#655dc6]/80">{t('confirm_new_password')}</Label>
              <div className="relative">
                <Input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-[#E8E6F5] border-[#C4BFF0] pe-10"
                  placeholder="******"
                  data-testid="confirm-password-input"
                />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute end-3 top-1/2 -translate-y-1/2 text-[#655dc6]/50">
                  {showConfirm ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading || !newPassword || !confirmPassword}
              className="w-full bg-[#655dc6] hover:bg-[#544cb0] text-white mt-2"
              data-testid="change-password-submit"
            >
              {loading ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" /> : t('change_password')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
